// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Steps } from './shared';

const DocCard: React.FC<{ title: string; description: string; to: string }> = ({
  title,
  description,
  to
}) => (
  <Link
    to={to}
    className="block no-underline border border-surface-hover rounded-[12px] p-[18px] bg-surface hover:border-blue hover:-translate-y-px hover:shadow-[0_4px_16px_rgba(34,211,238,0.1)] transition-all"
  >
    <h3 className="font-display text-[15px] font-bold text-content-primary mb-[6px] tracking-[-0.2px]">
      {title}
    </h3>
    <p className="m-0 text-[13px] text-slate-500 leading-[1.6]">{description}</p>
  </Link>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="font-display text-[12px] font-bold uppercase tracking-[0.06em] text-slate-400 mb-[12px] mt-[36px]">
    {children}
  </h2>
);

const DOC_LINK_CLASS = 'text-blue-link no-underline font-medium';

const EyeDocsLanding: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <h1 className="font-display text-[24px] md:text-[30px] font-extrabold text-content-primary mb-[14px] tracking-[-0.6px] break-words">
        {t('eyeDocs.landing.title')}
      </h1>
      <p className="text-[15px] leading-[1.7] text-content-secondary">
        {t('eyeDocs.landing.intro')}
      </p>

      <SectionTitle>{t('eyeDocs.landing.quickStart')}</SectionTitle>
      <Steps>
        <><Trans
            i18nKey="eyeDocs.landing.step1"
            components={{
              install: (
                <Link
                  to="/squarebit-eye/docs/installation"
                  className={DOC_LINK_CLASS}
                />
              )
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.landing.step2"
            components={{
              b: <strong />,
              rig: (
                <Link
                  to="/squarebit-eye/docs/rigging"
                  className={DOC_LINK_CLASS}
                />
              )
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.landing.step3"
            components={{
              params: (
                <Link
                  to="/squarebit-eye/docs/parameters"
                  className={DOC_LINK_CLASS}
                />
              ),
              tex: (
                <Link
                  to="/squarebit-eye/docs/textures"
                  className={DOC_LINK_CLASS}
                />
              )
            }}
          /></>
      </Steps>

      <SectionTitle>{t('eyeDocs.landing.gettingStarted')}</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px]">
        <DocCard
          title={t('eyeDocs.landing.installationTitle')}
          description={t('eyeDocs.landing.installationDesc')}
          to="/squarebit-eye/docs/installation"
        />
        <DocCard
          title={t('eyeDocs.landing.riggingTitle')}
          description={t('eyeDocs.landing.riggingDesc')}
          to="/squarebit-eye/docs/rigging"
        />
      </div>

      <SectionTitle>{t('eyeDocs.landing.reference')}</SectionTitle>
      <p className="text-[14px] leading-[1.7] text-content-secondary mb-[14px]">
        {t('eyeDocs.landing.referenceIntro')}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px]">
        <DocCard
          title={t('eyeDocs.landing.parametersTitle')}
          description={t('eyeDocs.landing.parametersDesc')}
          to="/squarebit-eye/docs/parameters"
        />
        <DocCard
          title={t('eyeDocs.landing.texturesTitle')}
          description={t('eyeDocs.landing.texturesDesc')}
          to="/squarebit-eye/docs/textures"
        />
      </div>

      <div className="border-t border-surface-hover pt-[24px] mt-[40px]">
        <p className="mb-[4px] text-[13px] font-semibold text-content-primary">
          {t('eyeDocs.landing.needHelp')}
        </p>
        <p className="m-0 text-[13px] text-slate-500 leading-[1.6]">
          <Trans
            i18nKey="eyeDocs.landing.needHelpText"
            components={{
              contact: <Link to="/contact" className={DOC_LINK_CLASS} />
            }}
          />
        </p>
      </div>
    </article>
  );
};

export default EyeDocsLanding;
